import { Fragment, useEffect, useState } from "react";
import { Container, Input } from "reactstrap";
import { Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { ProductList } from "../../Components/order/ProductList";
import { UserDeliverySection } from "../../Components/order/UserDeliverySection";
import { toast } from "react-toastify";
import { SelectedProductsCards } from "../../Components/order/SelectedProductsCards";
import DeliveryAddressSelector from "../../Components/order/DeliveryAddressSelector";

const DELIVERY_OPTIONS = [
  { value: "DELIVERY", icon: "ri-truck-line", label: "Domicilio", hint: "Se entrega en la dirección del cliente" },
  { value: "PHYSICAL", icon: "ri-store-2-line", label: "Recoger en tienda", hint: "El cliente recoge el pedido" },
];

export const CreateOrder = () => {
  const navigate = useNavigate();

  const [selectedUser, setSelectedUser] = useState(null);
  const [deliveryType, setDeliveryType] = useState("");
  const [selectedAddress, setSelectedAddress] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [shipping, setShipping] = useState(null);
  const [currency, setCurrency] = useState("COP");
  const [createdAt, setCreatedAt] = useState("");
  const [shippingCost, setShippingCost] = useState(0);

  const { getData, postData } = useDataFetch();

  const { mutateAsync: createOrder, isPending } = postData(
    "/order/byUserDash",
    {},
    {
      onSuccess: (response) => {
        if (response.message === "create_order_success") {
          toast.success("Orden creada exitosamente");
          navigate("/order");
        } else {
          toast.error("No se pudo crear la orden.");
        }
      },
    }
  );

  const {
    data: responseProductsByUser,
    isLoading: loadingProducts,
  } = getData(
    ["productsByUser", selectedUser?.value],
    selectedUser ? `/product/byUserDash/${selectedUser.value}` : null,
    {
      enabled: !!selectedUser?.value,
      staleTime: 0,
    }
  );

  const {
    data: responseAddressesByUser,
    isLoading: loadingAddresses,
  } = getData(
    ["addressesByUser", selectedUser?.value],
    selectedUser && deliveryType === "DELIVERY"
      ? `/address/byUserDash/${selectedUser.value}`
      : null,
    {
      enabled: !!selectedUser?.value && deliveryType === "DELIVERY",
      staleTime: 0,
    }
  );

  const productsByUser = responseProductsByUser?.products || [];
  const addresses = responseAddressesByUser?.data?.addresses || [];

  const isSelected = (productId) =>
    selectedProducts.some((p) => p.productId === productId);

  const toggleProduct = (productId, checked) => {
    if (checked) {
      setSelectedProducts((prev) => [...prev, { productId, quantity: 1 }]);
    } else {
      setSelectedProducts((prev) =>
        prev.filter((p) => p.productId !== productId)
      );
    }
  };

  const updateQuantity = (productId, quantity) => {
    setSelectedProducts((prev) =>
      prev.map((p) =>
        p.productId === productId
          ? { ...p, quantity: Math.max(1, parseInt(quantity)) }
          : p
      )
    );
  };

  const getProductDetails = (id) =>
    productsByUser.find((p) => p.id === id) || {};

  const getPrice = (product) =>
    parseFloat(product?.aditionalData?.[0]?.price_local_currency ?? 0);

  const getPoints = (product) =>
    parseFloat(product?.aditionalData?.[0]?.loyalty_points ?? 0);

  const subtotal = selectedProducts.reduce((acc, item) => {
    const product = getProductDetails(item.productId);
    const price = getPrice(product);
    return acc + price * item.quantity;
  }, 0);

  const pointsGenerated = selectedProducts.reduce((acc, item) => {
    const product = getProductDetails(item.productId);
    return acc + getPoints(product) * item.quantity;
  }, 0);


  useEffect(() => {
    if (deliveryType !== "DELIVERY") {
      setShippingCost(0);
      return;
    }

    if (selectedAddress && addresses.length) {
      const addr = addresses.find((a) => a.id === selectedAddress);
      if (addr) {
        const cost =
          addr.city_id?.shippingCost ?? addr.country_id?.shippingCost ?? 0;
        setShippingCost(cost);
      }
    }

  }, [deliveryType, selectedAddress, shipping, addresses]);

  const total = subtotal + shippingCost;

  const handleSubmit = async () => {
    if (!selectedUser) {
      toast.error("Debes seleccionar un usuario.");
      return;
    }

    if (!deliveryType) {
      toast.error("Debes seleccionar el tipo de entrega.");
      return;
    }

    if (deliveryType === "DELIVERY") {
      if (!selectedAddress && !shipping) {
        toast.error(
          "Debes ingresar una dirección de entrega o seleccionar una existente."
        );
        return;
      }

      if (!selectedAddress && shipping) {
        const requiredFields = [
          "name",
          "address",
          "country_id",
          "province_id",
          "city_id",
          "postal_code",
        ];

        const missingFields = requiredFields.filter(
          (field) =>
            !shipping[field] || shipping[field].toString().trim() === ""
        );

        if (missingFields.length > 0) {
          toast.error(
            "Completa todos los campos requeridos de la dirección manual."
          );
          return;
        }
      }
    }

    if (selectedProducts.length === 0) {
      toast.error("Debes seleccionar al menos un producto.");
      return;
    }

    const hasInvalidQuantity = selectedProducts.some(
      (item) => !item.quantity || item.quantity < 1
    );
    if (hasInvalidQuantity) {
      toast.error("Todas las cantidades deben ser mayores o iguales a 1.");
      return;
    }

    if (!paymentMethod) {
      toast.error("Debes seleccionar un método de pago.");
      return;
    }

    const body = {
      user_id: selectedUser.value,
      address_id: deliveryType === "DELIVERY" ? selectedAddress : null,
      shipping:
        deliveryType === "DELIVERY"
          ? !selectedAddress
            ? shipping
            : null
          : null,
      items: selectedProducts.map((item) => ({
        product_id: item.productId,
        quantity: item.quantity,
      })),
      payment_method: paymentMethod,
      delivery_type: deliveryType,
      currency,
      created_at: createdAt,
    };

    try {
      await createOrder(body);
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Error al crear la orden.");
    }
  };

  const handleIncrement = (productId) => {
    setSelectedProducts((prev) =>
      prev.map((item) =>
        item.productId === productId
          ? { ...item, quantity: item.quantity + 1 }
          : item
      )
    );
  };

  const handleDecrement = (productId) => {
    setSelectedProducts((prev) =>
      prev.map((item) =>
        item.productId === productId && item.quantity > 1
          ? { ...item, quantity: item.quantity - 1 }
          : item
      )
    );
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts((prev) =>
      prev.filter((item) => item.productId !== productId)
    );
  };

  const formatPrice = (value, currency = "COP") => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
    }).format(value);
  };

  useEffect(() => {
    setDeliveryType("");
    setSelectedAddress("");
    setSelectedProducts([]);
    setPaymentMethod("");
    setCreatedAt("");
  }, [selectedUser]);

  return (
    <Fragment>
      <Container fluid={true} className="ui-content" style={{ padding: 0 }}>
        <button type="button" className="ui-back" onClick={() => navigate(-1)}>
          <i className="ri-arrow-left-line" /> Volver a órdenes
        </button>

        <Form>
          <div className="ui-grid-main">
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <UserDeliverySection
                setSelectedUser={setSelectedUser}
                currency={currency}
                setCurrency={setCurrency}
                createdAt={createdAt}
                setCreatedAt={setCreatedAt}
              />

              <div className="ui-card">
                <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>2 · Productos</h3>

                {!selectedUser ? (
                  <p style={{ margin: 0, color: "#8A91AE", fontSize: 12.5 }}>
                    Selecciona un cliente para ver su catálogo de productos.
                  </p>
                ) : (
                  <>
                    <div style={{ marginBottom: 18 }}>
                      <ProductList
                        isSelected={isSelected}
                        loadingProducts={loadingProducts}
                        productsByUser={productsByUser}
                        selectedProducts={selectedProducts}
                        toggleProduct={toggleProduct}
                        updateQuantity={updateQuantity}
                        formatPrice={formatPrice}
                      />
                    </div>

                    <div style={{ paddingTop: 16, borderTop: "1px solid #EDEFF6" }}>
                      <h4 style={{ margin: "0 0 11px", fontSize: 13.5, fontWeight: 700 }}>En la orden</h4>
                      {selectedProducts.length === 0 ? (
                        <p style={{ margin: 0, color: "#8A91AE", fontSize: 12.5 }}>
                          No has seleccionado ningún producto.
                        </p>
                      ) : (
                        <SelectedProductsCards
                          selectedProducts={selectedProducts}
                          getProductDetails={getProductDetails}
                          getPrice={getPrice}
                          onIncrement={handleIncrement}
                          onDecrement={handleDecrement}
                          onRemove={handleRemoveProduct}
                          formatPrice={formatPrice}
                        />
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="ui-card">
                <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>3 · Entrega</h3>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {DELIVERY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`ui-radio-card${deliveryType === opt.value ? " ui-radio-card--active" : ""}`}
                      style={{ minWidth: 220 }}
                      onClick={() => setDeliveryType(opt.value)}
                    >
                      <i className={`${opt.icon} ui-radio-card__icon`} />
                      <div style={{ minWidth: 0 }}>
                        <div className="ui-radio-card__label">{opt.label}</div>
                        <div className="ui-radio-card__hint">{opt.hint}</div>
                      </div>
                      <span className="ui-radio-card__radio" />
                    </button>
                  ))}
                </div>

                {deliveryType === "DELIVERY" && (
                  <DeliveryAddressSelector
                    selectedUser={selectedUser}
                    addresses={addresses}
                    selectedAddress={selectedAddress}
                    setSelectedAddress={setSelectedAddress}
                    loadingAddresses={loadingAddresses}
                    setShipping={setShipping}
                    setShippingCost={setShippingCost}
                  />
                )}
              </div>
            </div>

            <div className="ui-card" style={{ position: "sticky", top: 92 }}>
              <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>Resumen</h3>
              <div className="ui-kv">
                {deliveryType === "DELIVERY" && (
                  <div className="ui-kv__row">
                    <span className="ui-kv__label">Envío</span>
                    <span className="ui-kv__value">{formatPrice(shippingCost, currency)}</span>
                  </div>
                )}
                <div className="ui-kv__row">
                  <span className="ui-kv__label">Subtotal</span>
                  <span className="ui-kv__value">{formatPrice(subtotal, currency)}</span>
                </div>
                <div className="ui-kv__row">
                  <span className="ui-kv__label">Puntos generados</span>
                  <span className="ui-kv__value" style={{ color: "#4B36F0" }}>{pointsGenerated} pts</span>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 15, paddingTop: 15, borderTop: "1px solid #EDEFF6" }}>
                <span style={{ fontSize: 13.5, fontWeight: 700 }}>Total</span>
                <span style={{ fontSize: 25, fontWeight: 800, letterSpacing: "-0.025em" }}>{formatPrice(total, currency)}</span>
              </div>

              <div style={{ marginTop: 16 }}>
                <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Método de pago</label>
                <Input type="select" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                  <option value="">Selecciona</option>
                  <option value="CASH">Efectivo</option>
                  <option value="TRANSFER">Transferencia</option>
                  <option value="LINK">Link de pago</option>
                </Input>
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isPending}
                style={{ width: "100%", marginTop: 16, border: "none", background: "#4B36F0", color: "#fff", borderRadius: 10, padding: "13px 0", fontFamily: "inherit", fontSize: 14, fontWeight: 700, cursor: isPending ? "default" : "pointer", opacity: isPending ? 0.7 : 1 }}
              >
                {isPending ? "Creando..." : "Crear orden"}
              </button>
              <button
                type="button"
                disabled
                title="Guardar borradores no está disponible todavía"
                style={{ width: "100%", marginTop: 9, border: "1px solid #E1E4F0", background: "#fff", color: "#C3C8DC", borderRadius: 10, padding: "12px 0", fontFamily: "inherit", fontSize: 13.5, fontWeight: 600, cursor: "not-allowed" }}
              >
                Guardar borrador
              </button>
            </div>
          </div>
        </Form>
      </Container>
    </Fragment>
  );
};
